import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, AlertTriangle, Sparkles, Clock,
  CheckCircle2, XCircle, HelpCircle, Star, MessageCircle,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * ClientReportView — post-cleaning report page at /cliente/reservas/:bid/relatorio.
 * Shows every photo grouped by room, plus all issues and extras reported during
 * the cleaning with their current decision state. Mirrors the email summary.
 */
const ROOM_META = {
  kitchen: { label: "Cozinha", accent: "text-[#d4af37]" },
  wc:       { label: "Casa de banho", accent: "text-[#d4af37]" },
  bedroom:  { label: "Quarto", accent: "text-[#d4af37]" },
  living:   { label: "Sala", accent: "text-[#d4af37]" },
};

const ISSUE_CAT = {
  damage: "Danos no alojamento",
  consumables: "Falta de consumíveis",
  lightbulb: "Lâmpadas fundidas",
  maintenance: "Manutenção",
  extra_cleaning: "Limpeza extra necessária",
  other: "Outro",
};

export default function ClientReportView() {
  const { bid } = useParams();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [{ data: d }, { data: r }] = await Promise.all([
        api.get(`/bookings/${bid}/report`),
        api.get(`/bookings/${bid}/rating`).catch(() => ({ data: {} })),
      ]);
      setData(d);
      setRating(r && r.stars ? r : null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);

  if (loading) return <div className="text-sm text-[#a0b2af]">A carregar relatório…</div>;
  if (!data) return (
    <div className="space-y-4" data-testid="report-not-found">
      <div className="text-sm text-[#fca5a5]">Relatório não encontrado.</div>
      <Link to="/cliente/reservas" className="text-xs uppercase tracking-widest text-[#d4af37] hover:underline">
        Voltar
      </Link>
    </div>
  );

  const { booking, photos, issues, extras } = data;
  const photosByRoom = photos.reduce((acc, p) => {
    (acc[p.room] = acc[p.room] || []).push(p); return acc;
  }, {});
  const roomKeys = Object.keys(ROOM_META);

  return (
    <div className="space-y-10" data-testid="client-report-view">
      <div>
        <Link to={`/cliente/reservas/${bid}`}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]"
              data-testid="back-to-booking">
          <ArrowLeft size={12} /> Voltar à reserva
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl mt-3">Relatório da limpeza</h1>
        <div className="text-[#a0b2af] text-sm mt-2">
          {booking.apartment_name || booking.apt_type} · {booking.scheduled_date} · {booking.scheduled_time}
          {booking.employee_duration_minutes ? (
            <span className="ml-2 inline-flex items-center gap-1 text-[#d4af37]">
              <Clock size={12} /> {booking.employee_duration_minutes} min
            </span>
          ) : null}
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3" data-testid="report-summary">
        <SummaryTile value={photos.length} label="Fotografias" icon={<Camera size={16} />} />
        <SummaryTile value={issues.length} label="Problemas" icon={<AlertTriangle size={16} />} />
        <SummaryTile value={extras.length} label="Extras sugeridos" icon={<Sparkles size={16} />} />
      </div>

      {/* RATING */}
      <RatingSection bid={bid} rating={rating} onChange={(r) => setRating(r)} />

      {/* PHOTOS */}
      <section className="space-y-5" data-testid="report-photos">
        <div className="eyebrow text-[#d4af37]">Fotografias por divisão</div>
        {photos.length === 0 ? (
          <div className="lux-card p-6 text-sm text-[#a0b2af]">Sem fotografias registadas.</div>
        ) : (
          roomKeys.map((rk) => {
            const rp = photosByRoom[rk] || [];
            if (rp.length === 0) return null;
            return (
              <div key={rk} className="lux-card p-5" data-testid={`report-room-${rk}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-xl">{ROOM_META[rk].label}</div>
                  <div className="text-xs text-[#a0b2af]">{rp.length} foto{rp.length === 1 ? "" : "s"}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {rp.map((p) => (
                    <button key={p.id}
                            onClick={() => setLightbox(p)}
                            className="relative aspect-square overflow-hidden border border-[#1a4640] hover:border-[#d4af37] transition-colors rounded-sm group"
                            data-testid={`report-photo-${p.id}`}>
                      <img src={`/api/files/${p.storage_path}`}
                           alt={`${ROOM_META[rk].label}`}
                           className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </section>

      {/* ISSUES */}
      <section className="space-y-3" data-testid="report-issues">
        <div className="eyebrow text-[#d4af37]">Problemas reportados</div>
        {issues.length === 0 ? (
          <div className="lux-card p-6 text-sm text-[#a0b2af]">Sem problemas durante esta limpeza.</div>
        ) : (
          issues.map((it) => <IssueCard key={it.id} item={it} />)
        )}
      </section>

      {/* EXTRAS */}
      <section className="space-y-3" data-testid="report-extras">
        <div className="eyebrow text-[#d4af37]">Extras sugeridos</div>
        {extras.length === 0 ? (
          <div className="lux-card p-6 text-sm text-[#a0b2af]">Sem extras sugeridos.</div>
        ) : (
          extras.map((ex) => <ExtraCard key={ex.id} item={ex} />)
        )}
      </section>

      {lightbox && (
        <div onClick={() => setLightbox(null)}
             className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
             data-testid="report-lightbox">
          <img src={`/api/files/${lightbox.storage_path}`}
               alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

function SummaryTile({ value, label, icon }) {
  return (
    <div className="lux-card p-4 text-center">
      <div className="inline-flex items-center gap-2 text-[#d4af37]">
        {icon}
      </div>
      <div className="font-display text-3xl text-[#d4af37] mt-1">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] mt-1">{label}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    open:             { label: "Reportado", icon: <HelpCircle size={10} />,  cls: "border-[#f59e0b]/50 text-[#f59e0b]" },
    acknowledged:     { label: "Em análise", icon: <HelpCircle size={10} />, cls: "border-[#3b82f6]/50 text-[#93c5fd]" },
    pending_client:   { label: "A aguardar a sua decisão", icon: <HelpCircle size={10} />, cls: "border-[#d4af37]/60 text-[#d4af37]" },
    client_approved:  { label: "Aprovado", icon: <CheckCircle2 size={10} />, cls: "border-[#10b981]/50 text-[#10b981]" },
    client_rejected:  { label: "Recusado", icon: <XCircle size={10} />,      cls: "border-[#ef4444]/50 text-[#fca5a5]" },
    resolved:         { label: "Resolvido", icon: <CheckCircle2 size={10} />,cls: "border-[#10b981]/50 text-[#10b981]" },
    pending:          { label: "Pendente", icon: <HelpCircle size={10} />,   cls: "border-[#f59e0b]/50 text-[#f59e0b]" },
    approved:         { label: "Aprovado", icon: <CheckCircle2 size={10} />, cls: "border-[#10b981]/50 text-[#10b981]" },
    rejected:         { label: "Recusado", icon: <XCircle size={10} />,      cls: "border-[#ef4444]/50 text-[#fca5a5]" },
    client_expired:   { label: "Expirado", icon: <XCircle size={10} />,      cls: "border-[#64748b]/50 text-[#94a3b8]" },
  };
  const s = map[status] || { label: status, icon: null, cls: "border-[#1a4640] text-[#a0b2af]" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm ${s.cls}`}>
      {s.icon} {s.label}
    </span>
  );
}

function PhotoThumbs({ photos }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {photos.map((ph, i) => (
        <a key={i} href={`/api/files/${ph.storage_path}`}
           target="_blank" rel="noreferrer"
           className="block w-16 h-16 border border-[#1a4640] hover:border-[#d4af37] overflow-hidden rounded-sm">
          <img src={`/api/files/${ph.storage_path}`} alt=""
               className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  );
}

function IssueCard({ item }) {
  const val = Number(item.admin_value || 0);
  return (
    <div className="lux-card p-5" data-testid={`report-issue-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs text-[#a0b2af] uppercase tracking-widest">
            {ISSUE_CAT[item.category] || item.category}
          </div>
          <div className="text-[#fdfbf7] mt-1">{item.description}</div>
          {item.admin_note && (
            <div className="text-xs text-[#a0b2af] mt-2 italic">Nota: {item.admin_note}</div>
          )}
          {item.client_note && (
            <div className="text-xs text-[#d4af37] mt-1">Sua nota: {item.client_note}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusChip status={item.status} />
          {val > 0 && (
            <div className="font-display text-lg text-[#d4af37]">{val.toFixed(2)} €</div>
          )}
        </div>
      </div>
      <PhotoThumbs photos={item.photos} />
    </div>
  );
}

function ExtraCard({ item }) {
  const priceSource = item.admin_price ?? item.suggested_price ?? 0;
  return (
    <div className="lux-card p-5" data-testid={`report-extra-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[#fdfbf7] font-medium">{item.label}</div>
          {item.note && (
            <div className="text-xs text-[#a0b2af] mt-1">{item.note}</div>
          )}
          {item.client_note && (
            <div className="text-xs text-[#d4af37] mt-1">Sua nota: {item.client_note}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusChip status={item.status} />
          {Number(priceSource) > 0 && (
            <div className="font-display text-lg text-[#d4af37]">{Number(priceSource).toFixed(2)} €</div>
          )}
        </div>
      </div>
      <PhotoThumbs photos={item.photos} />
    </div>
  );
}


function RatingSection({ bid, rating, onChange }) {
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(!rating);
  const [draft, setDraft] = useState({
    stars: rating?.stars || 0,
    sentiment: rating?.sentiment || null,
    comment: rating?.comment || "",
  });

  useEffect(() => {
    if (rating) setDraft({
      stars: rating.stars,
      sentiment: rating.sentiment || null,
      comment: rating.comment || "",
    });
  }, [rating]);

  async function submit(payload) {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bid}/rating`, payload);
      onChange?.(data);
      toast.success("Obrigado pelo seu feedback!");
      setExpanded(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }

  const effective = hover || draft.stars;

  return (
    <section className="lux-card p-6 md:p-7 border-[#d4af37]/50 bg-[#d4af37]/5"
             data-testid="report-rating">
      <div className="eyebrow flex items-center gap-2 text-[#d4af37]">
        <Star size={12} /> Como correu esta limpeza?
      </div>

      {!expanded && rating ? (
        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {[1,2,3,4,5].map(n => (
                <Star key={n} size={22}
                      className={n <= rating.stars ? "text-[#d4af37] fill-[#d4af37]" : "text-[#1a4640]"} />
              ))}
            </div>
            <div className="text-sm text-[#a0b2af]">
              Obrigado pelo feedback {rating.stars}★
            </div>
          </div>
          <button onClick={() => setExpanded(true)}
                  className="text-xs uppercase tracking-widest text-[#d4af37] hover:underline"
                  data-testid="rating-edit-btn">
            Editar
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          {/* Stars */}
          <div onMouseLeave={() => setHover(0)}
               className="flex items-center gap-2" data-testid="rating-stars">
            {[1,2,3,4,5].map(n => (
              <button key={n}
                      type="button"
                      onMouseEnter={() => setHover(n)}
                      onClick={() => setDraft(d => ({ ...d, stars: n }))}
                      className="p-1 transition-transform hover:scale-110"
                      aria-label={`${n} estrela${n === 1 ? "" : "s"}`}
                      data-testid={`rating-star-${n}`}>
                <Star size={34}
                      className={n <= effective
                        ? "text-[#d4af37] fill-[#d4af37] drop-shadow"
                        : "text-[#1a4640]"} />
              </button>
            ))}
            <span className="text-xs text-[#a0b2af] ml-2">
              {draft.stars ? `${draft.stars} / 5` : "Toque para avaliar"}
            </span>
          </div>

          {/* Sentiment buttons */}
          <div className="grid sm:grid-cols-2 gap-3">
            <button type="button"
                    onClick={() => submit({ stars: 5, sentiment: "excellent", comment: draft.comment || null })}
                    disabled={busy}
                    className="text-left p-4 border border-[#10b981]/40 hover:border-[#10b981] hover:bg-[#10b981]/10 rounded-sm transition-colors disabled:opacity-60"
                    data-testid="rating-excellent-btn">
              <div className="inline-flex items-center gap-2 text-[#10b981] font-medium">
                <CheckCircle2 size={16} /> Ficou excelente
              </div>
              <div className="text-xs text-[#a0b2af] mt-1">1 clique para dar 5★</div>
            </button>
            <button type="button"
                    onClick={() => submit({ stars: Math.min(draft.stars || 3, 3), sentiment: "concern", comment: draft.comment || null })}
                    disabled={busy}
                    className="text-left p-4 border border-[#f59e0b]/40 hover:border-[#f59e0b] hover:bg-[#f59e0b]/10 rounded-sm transition-colors disabled:opacity-60"
                    data-testid="rating-concern-btn">
              <div className="inline-flex items-center gap-2 text-[#f59e0b] font-medium">
                <AlertTriangle size={16} /> Tive uma preocupação
              </div>
              <div className="text-xs text-[#a0b2af] mt-1">Avisamos o admin para recuperar</div>
            </button>
          </div>

          {/* Optional comment */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] flex items-center gap-1.5 mb-2">
              <MessageCircle size={12} /> Comentário opcional
            </div>
            <textarea value={draft.comment}
                      onChange={(e) => setDraft(d => ({ ...d, comment: e.target.value }))}
                      maxLength={600}
                      rows={3}
                      placeholder="Diga-nos o que achou desta limpeza…"
                      className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm p-3 text-sm text-[#fdfbf7] resize-none"
                      data-testid="rating-comment" />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button type="button"
                    onClick={() => submit({
                      stars: draft.stars || 0,
                      sentiment: draft.sentiment,
                      comment: draft.comment || null,
                    })}
                    disabled={!draft.stars || busy}
                    className="btn-gold px-5 py-2.5 text-xs uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="rating-submit">
              {rating ? "Atualizar avaliação" : "Enviar avaliação"}
            </button>
            {rating && (
              <button type="button"
                      onClick={() => setExpanded(false)}
                      className="text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]"
                      data-testid="rating-cancel">
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
